/**
 * Iter 253c · Personal Information panel on My Profile.
 * Iter 253z-10 · Locked-by-default workflow:
 *   • Fields are read-only until user clicks "Request changes".
 *   • Submitted diffs land in `profile_edits` with status="pending" and
 *     the changed inputs re-lock with a YELLOW highlight (pending HR).
 *   • On HR approval the yellow highlight clears.
 *   • On HR rejection the affected fields highlight RED until the user
 *     re-edits them. Google Places autocomplete replaces the plain
 *     address textarea when REACT_APP_GOOGLE_MAPS_API_KEY is provided.
 */
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Locate, ShieldCheck, CheckCircle2, XCircle, Clock, Send, Lock, Pencil, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiErrorMessage } from "@/lib/apiError";

// Fix Leaflet's default marker icon paths (Webpack strips them by default)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

// Cache the Google Maps JS loader so multiple <AddressBlock/> instances
// share a single <script> tag and one .then() chain.
let _gmapsPromise = null;
function loadGoogleMaps() {
  if (!GOOGLE_MAPS_KEY) return Promise.reject(new Error("no key"));
  if (_gmapsPromise) return _gmapsPromise;
  _gmapsPromise = new Promise((resolve, reject) => {
    if (window.google?.maps?.places) { resolve(window.google); return; }
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_MAPS_KEY)}&libraries=places&v=weekly`;
    s.async = true; s.defer = true;
    s.onload = () => resolve(window.google);
    s.onerror = () => { _gmapsPromise = null; reject(new Error("gmaps load failed")); };
    document.head.appendChild(s);
  });
  return _gmapsPromise;
}

function LocationPicker({ position, onChange }) {
  useMapEvents({
    click(e) { onChange([e.latlng.lat, e.latlng.lng]); },
  });
  return position ? <Marker position={position} /> : null;
}

function AddressBlock({ label, testid, address, lat, lng, onChangeAddress, onChangeLat, onChangeLng, disabled, tone }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const position = (lat != null && lng != null) ? [lat, lng] : null;
  const toneCls = tone === "pending" ? "border-amber-400 bg-amber-50/40"
                : tone === "rejected" ? "border-rose-400 bg-rose-50/40"
                : "border-border";

  // Wire Google Places Autocomplete when a key is present.
  useEffect(() => {
    if (disabled || !GOOGLE_MAPS_KEY || !inputRef.current) return;
    let ac;
    let cancelled = false;
    loadGoogleMaps().then((google) => {
      if (cancelled || !inputRef.current) return;
      ac = new google.maps.places.Autocomplete(inputRef.current, {
        fields: ["formatted_address", "geometry"],
        types: ["geocode"],
      });
      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        if (place?.formatted_address) onChangeAddress(place.formatted_address);
        if (place?.geometry?.location) {
          onChangeLat(place.geometry.location.lat());
          onChangeLng(place.geometry.location.lng());
        }
      });
    }).catch(() => { /* graceful fallback → Nominatim locate-me still works */ });
    return () => { cancelled = true; if (ac) { try { window.google?.maps?.event?.clearInstanceListeners(ac); } catch { /* ignore */ } } };
     
  }, [disabled]);

  const locateMe = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const la = pos.coords.latitude, lo = pos.coords.longitude;
        onChangeLat(la); onChangeLng(lo);
        try {
          const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${la}&lon=${lo}&format=json&addressdetails=1`,
                                { headers: { "Accept": "application/json" } });
          const j = await r.json();
          if (j.display_name && !address) onChangeAddress(j.display_name);
        } catch { /* silent */ }
        setBusy(false);
      },
      () => { toast.error("Couldn't fetch location"); setBusy(false); },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  return (
    <div className={`border ${toneCls} p-3 space-y-2`} data-testid={testid} data-tone={tone || "clean"}>
      <div className="flex items-center justify-between">
        <div className="label-eyebrow flex items-center gap-1.5">
          {label}
          {tone === "pending" && <span className="text-[9px] text-amber-700 uppercase tracking-wide">Pending HR</span>}
          {tone === "rejected" && <span className="text-[9px] text-rose-700 uppercase tracking-wide">Rejected</span>}
          {disabled && <Lock className="w-3 h-3 text-muted-foreground" />}
        </div>
        <button type="button" onClick={locateMe} disabled={busy || disabled}
                 className="text-[11px] px-2 py-1 border border-border hover:border-primary hover:text-primary flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                 data-testid={`${testid}-locate`}>
          <Locate className="w-3 h-3" /> {busy ? "Locating…" : "Use my location"}
        </button>
      </div>
      <input
        ref={inputRef}
        type="text"
        value={address || ""}
        onChange={(e) => onChangeAddress(e.target.value)}
        placeholder={GOOGLE_MAPS_KEY ? "Start typing your address…" : "Street, city, state, pincode"}
        disabled={disabled}
        className="w-full text-sm border border-border p-2 focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-muted/40 disabled:cursor-not-allowed"
        data-testid={`${testid}-address`}
      />
      {GOOGLE_MAPS_KEY && !disabled && (
        <p className="text-[9px] text-muted-foreground italic">Suggestions powered by Google Places.</p>
      )}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[10px] text-muted-foreground">Latitude</div>
          <Input type="number" step="0.000001" value={lat ?? ""}
                    onChange={(e) => onChangeLat(e.target.value ? parseFloat(e.target.value) : null)}
                    disabled={disabled}
                    className="h-8 text-xs" data-testid={`${testid}-lat`} />
        </div>
        <div>
          <div className="text-[10px] text-muted-foreground">Longitude</div>
          <Input type="number" step="0.000001" value={lng ?? ""}
                    onChange={(e) => onChangeLng(e.target.value ? parseFloat(e.target.value) : null)}
                    disabled={disabled}
                    className="h-8 text-xs" data-testid={`${testid}-lng`} />
        </div>
      </div>
      <div className="h-52 border border-border" data-testid={`${testid}-map`}>
        <MapContainer center={position || [20.5937, 78.9629]} zoom={position ? 15 : 4}
                        style={{ height: "100%", width: "100%" }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {!disabled && <LocationPicker position={position}
                             onChange={([la, lo]) => { onChangeLat(la); onChangeLng(lo); }} />}
          {disabled && position && <Marker position={position} />}
        </MapContainer>
      </div>
      {!disabled && (
        <p className="text-[10px] text-muted-foreground italic">Click the map or use your current location to drop the pin.</p>
      )}
    </div>
  );
}


// ─── Field labels for the "pending changes" pretty list ────────────
const FIELD_LABELS = {
  phone: "Phone",
  personal_email: "Personal email",
  date_of_birth: "Date of birth",
  gender: "Gender",
  marital_status: "Marital status",
  blood_group: "Blood group",
  nationality: "Nationality",
  national_id: "National ID",
  wedding_anniversary: "Wedding anniversary",
  current_address: "Current address",
  current_lat: "Current latitude",
  current_lng: "Current longitude",
  permanent_address: "Permanent address",
  permanent_lat: "Permanent latitude",
  permanent_lng: "Permanent longitude",
  emergency_contact_name: "Emergency contact name",
  emergency_contact_relation: "Emergency contact relation",
  emergency_contact_phone: "Emergency contact phone",
  emergency_contact_email: "Emergency contact email",
};

function LockedField({ label, testid, tone, disabled, children }) {
  const toneCls = tone === "pending" ? "border-amber-400 bg-amber-50/40"
                : tone === "rejected" ? "border-rose-400 bg-rose-50/40"
                : "";
  return (
    <div className={`p-1.5 border ${toneCls || "border-transparent"}`} data-testid={testid} data-tone={tone || "clean"}>
      <div className="label-eyebrow mb-1 flex items-center gap-1.5">
        {label}
        {tone === "pending" && <span className="text-[9px] text-amber-700 uppercase tracking-wide">Pending HR</span>}
        {tone === "rejected" && <span className="text-[9px] text-rose-700 uppercase tracking-wide">Rejected</span>}
        {disabled && <Lock className="w-3 h-3 text-muted-foreground" />}
      </div>
      {children}
    </div>
  );
}

export default function PersonalInfoPanel() {
  const [pending, setPending] = useState(null);
  const [current, setCurrent] = useState({});
  const [lastReviewed, setLastReviewed] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpTargetEmail, setOtpTargetEmail] = useState("");
  // Iter 253z-10 · Fields are LOCKED by default. "Request changes" flips this
  // on. After a successful submit we auto-lock again so the user has to
  // consciously choose to open another change request.
  const [editMode, setEditMode] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/me/profile-edit`);
      setPending(data.pending || null);
      setCurrent(data.current || {});
      setLastReviewed(data.last_reviewed || null);
      const source = data.pending?.changes || {};
      const merged = { ...(data.current || {}), ...source };
      setDraft(merged);
    } catch (_e) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const dirtyFields = useMemo(() => {
    const out = {};
    for (const [k, v] of Object.entries(draft)) {
      if (current[k] !== v) out[k] = v;
    }
    return out;
  }, [draft, current]);

  const needsOtp = "personal_email" in dirtyFields;

  // Field-level tone lookup for yellow/red highlights.
  // - "pending" if the field is part of the currently-pending edit
  // - "rejected" if the last reviewed edit was rejected AND the field is in
  //   its `changes` AND the user hasn't touched it in this session
  const pendingKeys = useMemo(() => new Set(Object.keys(pending?.changes || {})), [pending]);
  const rejectedKeys = useMemo(() => {
    if (!lastReviewed || lastReviewed.status !== "rejected") return new Set();
    return new Set(Object.keys(lastReviewed.changes || {}));
  }, [lastReviewed]);

  const fieldTone = (key) => {
    if (pendingKeys.has(key)) return "pending";
    // Only show red until the user has changed this key in this session.
    if (rejectedKeys.has(key) && !(key in dirtyFields) && !pendingKeys.has(key)) return "rejected";
    return null;
  };

  const anyLocked = !editMode;

  // Individual field disabled = read-only unless the user is in edit mode.
  const disabled = !editMode;

  const sendOtp = async () => {
    if (!dirtyFields.personal_email) return;
    setOtpSending(true);
    try {
      await api.post(`/me/profile-edit/email-otp/send-to`, { email: dirtyFields.personal_email });
      setOtpTargetEmail(dirtyFields.personal_email);
      toast.success(`Code sent to ${dirtyFields.personal_email} · valid for 10 min`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to send code"));
    } finally { setOtpSending(false); }
  };

  const submit = async () => {
    if (Object.keys(dirtyFields).length === 0) {
      toast.error("Nothing to submit");
      return;
    }
    if (needsOtp && !otpCode) {
      toast.error("Enter the code sent to your new email");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/me/profile-edit`, {
        changes: dirtyFields,
        otp_code: needsOtp ? otpCode : null,
      });
      toast.success("Submitted for HR approval");
      setOtpCode(""); setOtpTargetEmail("");
      // Iter 253z-10 · Re-lock the form so the pending fields render read-only
      // in the amber "awaiting HR" state until HR acts on them.
      setEditMode(false);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Submit failed"));
    } finally { setSaving(false); }
  };

  const withdraw = async () => {
    if (!window.confirm("Withdraw your pending change?")) return;
    try {
      await api.delete(`/me/profile-edit`);
      toast.success("Withdrawn");
      // Reset draft back to server-current on withdraw.
      setEditMode(false);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Withdraw failed"));
    }
  };

  const cancelEdits = () => {
    // Reset any un-submitted edits back to the last-known-good state.
    const source = pending?.changes || {};
    setDraft({ ...(current || {}), ...source });
    setOtpCode(""); setOtpTargetEmail("");
    setEditMode(false);
  };

  return (
    <section className="border border-border p-4 space-y-3 mt-6" data-testid="personal-info-panel">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-primary" />
        <div className="font-bold text-sm">Personal information</div>
        {anyLocked ? (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
            <Lock className="w-3 h-3" /> Locked
          </span>
        ) : (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-primary">
            <Pencil className="w-3 h-3" /> Editing
          </span>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Fields are locked by default. Click <b>Request changes</b> to edit — updates are held for one-tap HR approval before they go live.
        Yellow = awaiting HR · Red = HR rejected (edit again to clear).
      </p>

      {!editMode && !pending && (
        <div className="flex justify-start">
          <button type="button" onClick={() => setEditMode(true)}
                   className="px-3 py-1.5 border border-primary text-primary bg-primary/5 hover:bg-primary/10 text-xs font-semibold inline-flex items-center gap-1.5"
                   data-testid="profile-request-changes-btn">
            <Pencil className="w-3.5 h-3.5" /> Request changes
          </button>
        </div>
      )}

      {pending && (
        <div className="border border-amber-400 bg-amber-50/40 p-3 text-xs space-y-1"
              data-testid="profile-pending-banner">
          <div className="flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-amber-700" />
            <b>{Object.keys(pending.changes || {}).length} change{Object.keys(pending.changes || {}).length === 1 ? "" : "s"} awaiting HR approval</b>
          </div>
          <ul className="pl-4 list-disc text-[11px]">
            {Object.entries(pending.changes || {}).map(([k, v]) => (
              <li key={k}><b>{FIELD_LABELS[k] || k}</b>: {String(pending.current_snapshot?.[k] ?? "—").slice(0, 40)} → <span className="text-primary">{String(v ?? "").slice(0, 40)}</span></li>
            ))}
          </ul>
          <button type="button" onClick={withdraw}
                   className="text-[10px] px-2 py-0.5 border border-border hover:border-rose-500 hover:text-rose-600 mt-1"
                   data-testid="profile-pending-withdraw">
            Withdraw request
          </button>
        </div>
      )}

      {lastReviewed && (
        <div className={`border ${lastReviewed.status === "approved" ? "border-emerald-300 bg-emerald-50/40" : "border-rose-300 bg-rose-50/40"} p-2.5 text-[11px]`}
              data-testid={`profile-last-${lastReviewed.status}`}>
          {lastReviewed.status === "approved"
            ? <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Your previous change was <b>approved</b> by {lastReviewed.reviewed_by_name} on {lastReviewed.reviewed_at?.slice(0,10)}.</span>
            : <span className="flex items-center gap-1.5"><XCircle className="w-3.5 h-3.5 text-rose-600" /> Your previous change was <b>rejected</b> by {lastReviewed.reviewed_by_name}: <i>{lastReviewed.review_note}</i></span>}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <LockedField label="Phone" testid="profile-phone-field" tone={fieldTone("phone")} disabled={disabled}>
          <Input value={draft.phone || ""} onChange={(e) => set("phone", e.target.value)}
                    disabled={disabled}
                    data-testid="profile-phone-input" />
        </LockedField>
        <LockedField label={<>Personal email <span className="text-[9px] text-amber-600">🔒 OTP</span></>}
                    testid="profile-personal-email-field" tone={fieldTone("personal_email")} disabled={disabled}>
          <Input value={draft.personal_email || ""} onChange={(e) => set("personal_email", e.target.value)}
                    disabled={disabled}
                    type="email" data-testid="profile-personal-email-input" />
        </LockedField>
      </div>

      {needsOtp && (
        <div className="border border-amber-400 bg-amber-50/30 p-3 space-y-2" data-testid="profile-otp-block">
          <div className="text-[11px] font-semibold">Email change requires OTP verification</div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={sendOtp} disabled={otpSending}
                     className="text-[11px] px-2 py-1 border border-amber-600 bg-amber-600 text-white hover:bg-amber-700 flex items-center gap-1 disabled:opacity-50"
                     data-testid="profile-otp-send-btn">
              <Send className="w-3 h-3" /> {otpSending ? "Sending…" : (otpTargetEmail === dirtyFields.personal_email ? "Resend code" : "Send code")}
            </button>
            <Input placeholder="6-digit code" value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      maxLength={6} className="h-8 text-xs w-32 font-mono"
                      data-testid="profile-otp-input" />
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <LockedField label="Date of birth" testid="profile-dob-field" tone={fieldTone("date_of_birth")} disabled={disabled}>
          <Input type="date" value={draft.date_of_birth || ""} onChange={(e) => set("date_of_birth", e.target.value)}
                    disabled={disabled}
                    data-testid="profile-dob-input" />
        </LockedField>
        <LockedField label="Gender" testid="profile-gender-field" tone={fieldTone("gender")} disabled={disabled}>
          <Select value={draft.gender || ""} onValueChange={(v) => set("gender", v)} disabled={disabled}>
            <SelectTrigger data-testid="profile-gender-input"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
              <SelectItem value="other">Other</SelectItem>
              <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
            </SelectContent>
          </Select>
        </LockedField>
        <LockedField label="Marital status" testid="profile-marital-field" tone={fieldTone("marital_status")} disabled={disabled}>
          <Select value={draft.marital_status || ""} onValueChange={(v) => set("marital_status", v)} disabled={disabled}>
            <SelectTrigger data-testid="profile-marital-input"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="single">Single</SelectItem>
              <SelectItem value="married">Married</SelectItem>
              <SelectItem value="divorced">Divorced</SelectItem>
              <SelectItem value="widowed">Widowed</SelectItem>
            </SelectContent>
          </Select>
        </LockedField>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <LockedField label="Blood group" testid="profile-blood-field" tone={fieldTone("blood_group")} disabled={disabled}>
          <Input value={draft.blood_group || ""} onChange={(e) => set("blood_group", e.target.value)}
                    disabled={disabled}
                    placeholder="e.g. O+" data-testid="profile-blood-input" />
        </LockedField>
        <LockedField label="Nationality" testid="profile-nationality-field" tone={fieldTone("nationality")} disabled={disabled}>
          <Input value={draft.nationality || ""} onChange={(e) => set("nationality", e.target.value)}
                    disabled={disabled}
                    data-testid="profile-nationality-input" />
        </LockedField>
        <LockedField label="National ID / PAN" testid="profile-national-id-field" tone={fieldTone("national_id")} disabled={disabled}>
          <Input value={draft.national_id || ""} onChange={(e) => set("national_id", e.target.value)}
                    disabled={disabled}
                    data-testid="profile-national-id-input" />
        </LockedField>
      </div>

      <AddressBlock
        label="Current address"
        testid="profile-current-address"
        address={draft.current_address}
        lat={draft.current_lat}
        lng={draft.current_lng}
        onChangeAddress={(v) => set("current_address", v)}
        onChangeLat={(v) => set("current_lat", v)}
        onChangeLng={(v) => set("current_lng", v)}
        disabled={disabled}
        tone={fieldTone("current_address") || fieldTone("current_lat") || fieldTone("current_lng")}
      />

      <AddressBlock
        label="Permanent address"
        testid="profile-permanent-address"
        address={draft.permanent_address}
        lat={draft.permanent_lat}
        lng={draft.permanent_lng}
        onChangeAddress={(v) => set("permanent_address", v)}
        onChangeLat={(v) => set("permanent_lat", v)}
        onChangeLng={(v) => set("permanent_lng", v)}
        disabled={disabled}
        tone={fieldTone("permanent_address") || fieldTone("permanent_lat") || fieldTone("permanent_lng")}
      />

      <div className="border border-border p-3 space-y-2">
        <div className="label-eyebrow flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Emergency contact</div>
        <div className="grid grid-cols-2 gap-2">
          <LockedField label="Name" testid="profile-ec-name-field" tone={fieldTone("emergency_contact_name")} disabled={disabled}>
            <Input value={draft.emergency_contact_name || ""} onChange={(e) => set("emergency_contact_name", e.target.value)}
                      disabled={disabled}
                      placeholder="Full name" data-testid="profile-ec-name" />
          </LockedField>
          <LockedField label="Relation" testid="profile-ec-relation-field" tone={fieldTone("emergency_contact_relation")} disabled={disabled}>
            <Input value={draft.emergency_contact_relation || ""} onChange={(e) => set("emergency_contact_relation", e.target.value)}
                      disabled={disabled}
                      placeholder="Relation" data-testid="profile-ec-relation" />
          </LockedField>
          <LockedField label="Phone" testid="profile-ec-phone-field" tone={fieldTone("emergency_contact_phone")} disabled={disabled}>
            <Input value={draft.emergency_contact_phone || ""} onChange={(e) => set("emergency_contact_phone", e.target.value)}
                      disabled={disabled}
                      placeholder="Phone" data-testid="profile-ec-phone" />
          </LockedField>
          <LockedField label="Email" testid="profile-ec-email-field" tone={fieldTone("emergency_contact_email")} disabled={disabled}>
            <Input value={draft.emergency_contact_email || ""} onChange={(e) => set("emergency_contact_email", e.target.value)}
                      disabled={disabled}
                      placeholder="Email" data-testid="profile-ec-email" />
          </LockedField>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-border">
        <div className="text-xs text-muted-foreground">
          {Object.keys(dirtyFields).length > 0
            ? `${Object.keys(dirtyFields).length} field${Object.keys(dirtyFields).length === 1 ? "" : "s"} to submit for approval`
            : editMode ? "Make edits above, then submit for approval" : "No changes yet"}
        </div>
        <div className="flex items-center gap-2">
          {editMode && (
            <button type="button" onClick={cancelEdits}
                     className="px-3 py-2 border border-border text-xs hover:bg-muted inline-flex items-center gap-1.5"
                     data-testid="profile-cancel-edits-btn">
              <RotateCcw className="w-3.5 h-3.5" /> Cancel
            </button>
          )}
          {editMode && (
            <button type="button" onClick={submit} disabled={saving || Object.keys(dirtyFields).length === 0}
                     className="px-4 py-2 border border-primary bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50"
                     data-testid="profile-info-submit-btn">
              {saving ? "Submitting…" : "Submit for HR approval"}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
